import React, { useMemo, useState } from 'react';
import { isoDay, formatDayLabel, localBucketDay } from '../lib/historyMeta';

/**
 * GitHub-style 53-week × 7-day heatmap, anchored to a year ending today.
 *
 * Props:
 *   - events: [{ date: 'YYYY-MM-DD', event_type, ... }, ...]
 *   - onDayClick: (isoDay, eventsForThatDay) => void
 *   - endDate?: Date (defaults to today; the grid spans 53 weeks ending here)
 */
const DAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', ''];
const WEEKS = 53;

const INTENSITY_CLASSES = [
  'bg-slate-100',                    // 0 events
  'bg-emerald-200',                  // 1
  'bg-emerald-400',                  // 2-3
  'bg-emerald-600',                  // 4-6
  'bg-emerald-800',                  // 7+
];

function intensity(count) {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count <= 3) return 2;
  if (count <= 6) return 3;
  return 4;
}

const YearHeatmap = ({ events, onDayClick, endDate }) => {
  const [hovered, setHovered] = useState(null);

  // Map: ISO day → event list
  const byDay = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const day = localBucketDay(e);
      if (!day) return;
      if (!m.has(day)) m.set(day, []);
      m.get(day).push(e);
    });
    return m;
  }, [events]);

  // Build the 53-week × 7-day grid, ending at the end of the week containing `endDate`.
  const cells = useMemo(() => {
    const end = endDate ? new Date(endDate) : new Date();
    // Slide forward to the Sunday at/after `end`.
    const endSunday = new Date(end);
    endSunday.setDate(endSunday.getDate() + (6 - endSunday.getDay()));
    // 53 weeks * 7 days back from `endSunday` (Mon-Sun column ordering, Sun last).
    const cols = [];
    for (let w = WEEKS - 1; w >= 0; w--) {
      const col = [];
      for (let dow = 0; dow < 7; dow++) {
        const d = new Date(endSunday);
        d.setDate(endSunday.getDate() - w * 7 - (6 - dow));
        col.push({
          iso: isoDay(d),
          date: d,
          // Layout: top row = Mon (dow 0), bottom row = Sun (dow 6) using ISO-ish week sense.
          // We'll re-map below to match DAY_LABELS.
          dow,
        });
      }
      cols.push(col);
    }
    return cols;
  }, [endDate]);

  // Month label positions: pick the first column of each month transition.
  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = null;
    cells.forEach((col, idx) => {
      const top = col[0].date;
      const monthKey = `${top.getFullYear()}-${top.getMonth()}`;
      if (monthKey !== lastMonth) {
        labels.push({
          idx,
          label: top.toLocaleDateString(undefined, { month: 'short' }),
        });
        lastMonth = monthKey;
      }
    });
    // Drop labels that are within 1 column of the previous (too dense)
    return labels.filter((l, i, arr) => i === 0 || l.idx - arr[i - 1].idx > 1);
  }, [cells]);

  return (
    <div className="w-full overflow-x-auto" data-testid="year-heatmap">
      <div className="inline-block min-w-full">
        {/* Month labels row */}
        <div className="grid gap-[3px] pl-7" style={{ gridTemplateColumns: `repeat(${WEEKS}, 12px)` }}>
          {cells.map((_, idx) => {
            const m = monthLabels.find((x) => x.idx === idx);
            return (
              <div key={idx} className="h-3 text-[10px] text-slate-500 leading-3">
                {m ? m.label : ''}
              </div>
            );
          })}
        </div>

        <div className="flex gap-1 mt-1">
          {/* Day-of-week labels */}
          <div className="grid grid-rows-7 gap-[3px] text-[10px] text-slate-500 pr-1 w-6">
            {DAY_LABELS.map((d, i) => (
              <div key={i} className="h-3 leading-3">{d}</div>
            ))}
          </div>

          {/* Grid */}
          <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${WEEKS}, 12px)` }}>
            {cells.map((col, ci) => (
              <div key={ci} className="grid grid-rows-7 gap-[3px]">
                {col.map((cell) => {
                  const dayEvents = byDay.get(cell.iso) || [];
                  const cls = INTENSITY_CLASSES[intensity(dayEvents.length)];
                  const isFuture = cell.date > new Date();
                  const isHovered = hovered === cell.iso;
                  return (
                    <button
                      key={cell.iso}
                      type="button"
                      onMouseEnter={() => setHovered(cell.iso)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => onDayClick && onDayClick(cell.iso, dayEvents)}
                      data-testid={`heatmap-cell-${cell.iso}`}
                      data-count={dayEvents.length}
                      className={`h-3 w-3 rounded-sm ${cls} ${
                        isFuture ? 'opacity-50' : ''
                      } ${isHovered ? 'ring-2 ring-slate-700 ring-offset-1' : ''} transition`}
                      title={`${formatDayLabel(cell.iso)} — ${dayEvents.length} event${
                        dayEvents.length === 1 ? '' : 's'
                      }`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-2 mt-3 text-[11px] text-slate-500">
          <span>Less</span>
          {INTENSITY_CLASSES.map((c, i) => (
            <span key={i} className={`h-3 w-3 rounded-sm inline-block ${c}`} />
          ))}
          <span>More</span>
        </div>
      </div>
    </div>
  );
};

export default YearHeatmap;
